import React, { useRef, useState } from "react";
import { Download, Loader2, Share2 } from "lucide-react";

interface ChartDownloadButtonProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  portfolioName?: string;
  appearance?: "default" | "subtle";
}

export default function ChartDownloadButton({ containerRef, portfolioName, appearance = "default" }: ChartDownloadButtonProps) {
  const buttonColors = appearance === "subtle"
    ? "border-transparent bg-transparent text-v7-ink-soft hover:bg-v7-paper hover:text-v7-ink"
    : "border-gray-200 bg-white text-gray-600 hover:bg-gray-100 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:bg-rp-surface-raised";
  const latestPortfolioName = useRef(portfolioName);
  latestPortfolioName.current = portfolioName;
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareReady, setShareReady] = useState(false);
  const preparedShare = useRef<{ file: File; markup: string; name?: string } | null>(null);
  const supportsShare = typeof navigator.share === "function"
    && typeof navigator.canShare === "function"
    && navigator.canShare({ files: [new File([""], "chart.png", { type: "image/png" })] });

  const downloadFile = (href: string, filename: string) => {
    const link = document.createElement("a");
    link.download = filename;
    link.href = href;
    link.click();
  };

  const shareFile = async (file: File) => {
    try {
      await navigator.share({ files: [file] });
      if (latestPortfolioName.current !== portfolioName) return;
      preparedShare.current = null;
      setShareReady(false);
    } catch (error) {
      if (latestPortfolioName.current !== portfolioName) return;
      if (error instanceof DOMException && error.name === "AbortError") return;
      setError("Sharing failed. Please try again or download the image.");
    }
  };

  const exportChart = async (action: "download" | "share") => {
    const target = containerRef.current;
    if (!target || downloading) return;
    setError(null);
    if (action === "share" && preparedShare.current?.markup === target.innerHTML
      && preparedShare.current.name === portfolioName) {
      setDownloading(true);
      await shareFile(preparedShare.current.file);
      setDownloading(false);
      return;
    }
    preparedShare.current = null;
    setShareReady(false);
    setDownloading(true);
    const markup = target.innerHTML;
    try {
      await document.fonts?.ready;
      await Promise.all(Array.from(target.querySelectorAll("img"), image => image.decode()));
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(target, {
        scale: 2,
        // Use browser text/layout rendering to preserve flex alignment and avoid
        // clipping text in the compact header.
        foreignObjectRendering: true,
        logging: false,
        x: 0,
        y: 0,
        scrollX: 0,
        scrollY: 0,
        backgroundColor: getComputedStyle(target).backgroundColor,
        onclone: (_document, clonedTarget) => {
          // Render the clone at its own origin so page offsets cannot crop it.
          Object.assign(clonedTarget.style, {
            position: "fixed", left: "0", top: "0", margin: "0",
            width: `${target.offsetWidth}px`, height: `${target.offsetHeight}px`,
          });
          // Native SVG image rendering needs embedded watermark assets.
          const originalImages = target.querySelectorAll("img");
          clonedTarget.querySelectorAll("img").forEach((image, index) => {
            const original = originalImages[index];
            const asset = document.createElement("canvas");
            asset.width = original.naturalWidth;
            asset.height = original.naturalHeight;
            const context = asset.getContext("2d");
            if (!context || !asset.width || !asset.height) throw new Error("Chart image is not ready");
            context.drawImage(original, 0, 0);
            image.src = asset.toDataURL("image/png");
            image.removeAttribute("srcset");
          });
          // SVGs are serialized as images by html2canvas; inline their resolved
          // styles so chart tokens and fonts survive outside the page stylesheet.
          const originalNodes = target.querySelectorAll("svg, svg *");
          clonedTarget.querySelectorAll<SVGElement>("svg, svg *").forEach((node, index) => {
            const styles = getComputedStyle(originalNodes[index]);
            ["stroke", "fill", "color", "font-family", "font-size", "font-weight"].forEach(property => {
              node.style.setProperty(property, styles.getPropertyValue(property));
            });
          });
        },
      });
      if (target !== containerRef.current || markup !== target.innerHTML
        || latestPortfolioName.current !== portfolioName) return;
      const filename = `${(portfolioName || "performance-chart").replace(/[^a-z0-9_-]+/gi, "-")}.png`;
      if (action === "share" && supportsShare) {
        const blob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob(value => value ? resolve(value) : reject(new Error("PNG export failed")), "image/png");
        });
        if (target !== containerRef.current || markup !== target.innerHTML
          || latestPortfolioName.current !== portfolioName) return;
        const file = new File([blob], filename, { type: "image/png" });
        if (!navigator.canShare({ files: [file] })) {
          downloadFile(canvas.toDataURL("image/png"), filename);
          return;
        }
        preparedShare.current = { file, markup, name: portfolioName };
        // Rendering can outlast the browser's click permission. A fresh click
        // on Share opens the share sheet directly with the prepared image.
        if (navigator.userActivation?.isActive) {
          await shareFile(file);
        } else {
          setShareReady(true);
        }
        return;
      }
      downloadFile(canvas.toDataURL("image/png"), filename);
    } catch {
      if (latestPortfolioName.current !== portfolioName || target !== containerRef.current
        || markup !== target.innerHTML) return;
      setError(action === "share" ? "Could not prepare the image. Please try again." : "Download failed. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <span className={`inline-flex items-center gap-2 ${appearance === "subtle" ? "max-w-40 flex-wrap justify-end" : ""}`}>
      <button type="button" onClick={() => exportChart("download")} disabled={downloading}
        aria-label="Download chart as PNG" title="Download chart as PNG" aria-busy={downloading}
        className={`inline-flex items-center gap-1 rounded-full border p-1.5 text-xs font-medium disabled:opacity-50 ${buttonColors}`}>
        {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Download className="h-3.5 w-3.5" aria-hidden="true" />}
      </button>
      <button type="button" onClick={() => exportChart("share")} disabled={downloading}
        aria-label="Share chart image" title="Share chart image"
        className={`inline-flex items-center rounded-full border p-1.5 disabled:opacity-50 ${buttonColors}`}>
        <Share2 className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      {shareReady && <span role="status" className={`text-xs ${appearance === "subtle" ? "text-v7-ink-soft" : "text-gray-600 dark:text-gray-300"}`}>Image ready. Click Share to choose an app.</span>}
      {error && <span role="alert" className={`text-xs ${appearance === "subtle" ? "text-red-700" : "text-red-700 dark:text-red-400"}`}>{error}</span>}
    </span>
  );
}
